/**
 * Opens the two-step start by itself, once per browser-tab session, for a
 * member who lands on the sessions page with a step still open for them
 * (FW-31): a new organisation's admin, or an invited member without an
 * agent login. Nothing else: not a deep link (only the landing page), not
 * someone whose steps are done or skipped (existing users who finished
 * setup never see it), not after "Don't remind me again", and not a
 * platform admin looking around. Renders nothing.
 */
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useMe } from "@/api/hooks";

import { autoShownThisSession, markAutoShown } from "./onboardingFlowState";
import { useOnboardingFlow } from "./useOnboardingFlow";
import { useSetupNudgeDismissed } from "./useSetupNudge";

const LANDING = "/app/sessions";
const WELCOME = "/app/welcome";

export function OnboardingAutoShow(): null {
  const me = useMe();
  const flow = useOnboardingFlow();
  const dismissed = useSetupNudgeDismissed(flow.slug);
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const globalAdmin = Boolean(me.data?.admin_scope?.is_global_admin);

  const show =
    // The bare landing page only: a shared or filtered sessions link
    // (with a query) is left alone.
    pathname === LANDING &&
    search === "" &&
    flow.ready &&
    flow.isMember &&
    flow.pending &&
    !dismissed &&
    !globalAdmin &&
    flow.slug !== null &&
    !autoShownThisSession(flow.slug);

  React.useEffect(() => {
    if (!show || !flow.slug) return;
    markAutoShown(flow.slug);
    // A push, not a replace: Back returns to the sessions page, and the
    // session flag keeps it there.
    navigate(WELCOME);
  }, [show, flow.slug, navigate]);

  return null;
}
